import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ChevronRight, PlayCircle, RotateCcw, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface PremiumResumeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assessmentTitle: string;
  completionPercentage?: number;
  lastUpdatedAt?: string | null;
  onResume: () => void;
  onStartNew: () => void;
}

export default function PremiumResumeDialog({
  open,
  onOpenChange,
  assessmentTitle,
  completionPercentage = 0,
  lastUpdatedAt,
  onResume,
  onStartNew,
}: PremiumResumeDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ongoing Assessment Found</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          You have an ongoing <span className="font-medium text-foreground">{assessmentTitle}</span> assessment. Would you like to continue where you left off or start fresh?
        </p>

        {/* Progress summary */}
        <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-foreground">Overall completion</span>
            <span className="text-primary font-semibold">{completionPercentage}%</span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          {lastUpdatedAt && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground pt-1">
              <Clock className="h-3.5 w-3.5" />
              <span>Last updated {format(new Date(lastUpdatedAt), 'MMM dd, yyyy • h:mm a')}</span>
            </div>
          )}
        </div>

        <div className="space-y-3">
          <Card
            className="cursor-pointer hover:border-primary/30 transition-colors"
            onClick={onResume}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary shrink-0">
                  <PlayCircle className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">Continue Assessment</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Pick up where you left off with your saved progress.
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 ml-3" />
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:border-primary/30 transition-colors"
            onClick={onStartNew}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-primary/10 text-primary shrink-0">
                  <RotateCcw className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-medium text-foreground">Start New Assessment</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Begin a fresh assessment. Your saved progress will be discarded.
                  </p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 ml-3" />
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
